import {action, computed, computedFn } from '@api'
import {BorderNode, DropInfo, FlexLayoutNode, IDraggable, ILayoutMetrics, Model, Orientation, Rect} from '../'

export class BorderSet {
  /** @hidden @internal */
  static _fromJson(json: any, model: Model) {
    const borderSet = new BorderSet(model)
    borderSet._borders = json.map((borderJson: any) => BorderNode._fromJson(borderJson, model))
    return borderSet
  }

  /** @hidden @internal */
  private _model: Model
  /** @hidden @internal */
  private _borders: BorderNode[]

  /** @hidden @internal */
  constructor(model: Model) {
    this._model = model
    this._borders = []
  }

  get borders() {
    return this._borders
  }

  /** @hidden @internal */
  _forEachNode = (fn: (node: FlexLayoutNode, level: number) => void) => {
    this._borders.forEach((borderNode) => {
      fn(borderNode, 0)
      borderNode.children.forEach((node) => {
        node._forEachNode(fn, 1)
      })
    })
  }

  /** @hidden @internal */
  @computed get asJson() {
    return this._borders.map((borderNode) => borderNode.asJson)
  }

  /** @hidden @internal */
  @action _layoutBorder = (outerInnerRects: { inner: Rect; outer: Rect }, metrics: ILayoutMetrics) => {
    const rect = outerInnerRects.outer
    const height = rect.height
    const width = rect.width
    let sumHeight = 0
    let sumWidth = 0
    let adjustableHeight = 0
    let adjustableWidth = 0

    const showingBorders = this._borders.filter((border) => border.isShowing)

    // sum size of borders to see they will fit
    for (const border of showingBorders) {
      if (border.isShowing) {
        border._setAdjustedSize(border.size)
        const visible = border.selected !== -1
        if (border.location.orientation === Orientation.HORZ) {
          sumWidth += border.borderBarSize + this._model.splitterSize
          if (visible) {
            sumWidth += border.size
            adjustableWidth += border.size
          }
        } else {
          sumHeight += border.borderBarSize + this._model.splitterSize
          if (visible) {
            sumHeight += border.size
            adjustableHeight += border.size
          }
        }
      }
    }

    // adjust border sizes if too large
    let j = 0
    while ((sumWidth > width && adjustableWidth > 0) || (sumHeight > height && adjustableHeight > 0)) {
      const border = showingBorders[j]
      if (border.selected !== -1) {
        // visible
        const size = border._getAdjustedSize()
        if (sumWidth > width && adjustableWidth > 0 && border.location.orientation === Orientation.HORZ && size > 0) {
          border._setAdjustedSize(size - 1)
          sumWidth--
          adjustableWidth--
        } else if (sumHeight > height && adjustableHeight > 0 && border.location.orientation === Orientation.VERT && size > 0) {
          border._setAdjustedSize(size - 1)
          sumHeight--
          adjustableHeight--
        }
      }
      j = (j + 1) % showingBorders.length
    }

    showingBorders.forEach((border) => {
      outerInnerRects.outer = border._layoutBorderOuter(outerInnerRects.outer, metrics)
    })

    outerInnerRects.inner = outerInnerRects.outer

    showingBorders.forEach((border) => {
      outerInnerRects.inner = border._layoutBorderInner(outerInnerRects.inner, metrics)
    })
    return outerInnerRects
  }

  /** @hidden @internal */
  _findDropTargetNode = (dragNode: FlexLayoutNode & IDraggable, x: number, y: number): DropInfo | undefined => {
    for (const border of this._borders) {
      if (border.isShowing) {
        const dropInfo = border.canDrop(dragNode, x, y)
        if (dropInfo !== undefined) {
          return dropInfo
        }
      }
    }
    return undefined
  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/model/BorderSet.ts